import React, { useState } from 'react';
import {
  AppBar, Toolbar, Typography, Box, Link,
  useMediaQuery, Button, IconButton, Drawer, List,
  ListItem, ListItemText, Divider, Avatar, Menu, MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import InstagramIcon from '@mui/icons-material/Instagram';
import Logo from '../common/Logo';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const { user, logout } = useAuth();

  const navLinks = [
    { label: t('nav.marketplace'), to: '/mercato' },
    { label: t('nav.contact'), to: '/contatti' },
  ];

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: 'background.paper',
        color: 'text.primary',
        boxShadow: 2,
        paddingTop: '30px',
      }}
    >
      {/* Top bordeaux bar */}
      <Box
        sx={{
          height: '30px',
          backgroundColor: '#951520',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 1.5, sm: 4 },
          position: 'absolute',
          top: 0, left: 0, right: 0, width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'white', minWidth: 0 }}>
          {!isXs && (
            <Typography variant="body2" component="span" sx={{ mr: 1, fontSize: { xs: '0.7rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>
              Segui Watch Passion Arezzo sui social
            </Typography>
          )}
          <Link href="https://www.instagram.com/watch_passion/?hl=it" target="_blank" rel="noopener noreferrer" color="inherit" sx={{ mx: 0.5, display: 'inline-flex' }}>
            <InstagramIcon sx={{ color: 'white', fontSize: '20px' }} />
          </Link>
        </Box>

        {/* Language switcher */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Button
            onClick={() => i18n.changeLanguage('en')}
            sx={{
              minWidth: 'auto', p: '4px 8px', fontSize: '0.75rem',
              color: i18n.language === 'en' ? 'white' : 'rgba(255,255,255,0.5)',
              fontWeight: i18n.language === 'en' ? 'bold' : 'normal',
              textTransform: 'none',
            }}
          >
            🇬🇧 EN
          </Button>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>|</Typography>
          <Button
            onClick={() => i18n.changeLanguage('it')}
            sx={{
              minWidth: 'auto', p: '4px 8px', fontSize: '0.75rem',
              color: i18n.language === 'it' ? 'white' : 'rgba(255,255,255,0.5)',
              fontWeight: i18n.language === 'it' ? 'bold' : 'normal',
              textTransform: 'none',
            }}
          >
            🇮🇹 IT
          </Button>
        </Box>
      </Box>

      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <RouterLink to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
            <Logo size={40} />
          </RouterLink>
        </Box>

        {!isMobile && (
          <Box sx={{ display: 'flex', ml: 4 }}>
            {navLinks.map(link => (
              <RouterLink key={link.to} to={link.to} style={{ textDecoration: 'none', margin: '0 8px' }}>
                <Typography sx={{ fontSize: '1.05rem', color: 'text.primary', '&:hover': { color: '#951520' } }}>
                  {link.label}
                </Typography>
              </RouterLink>
            ))}
          </Box>
        )}

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          {user ? (
            <>
              {user.role === 'admin' && (
                <IconButton onClick={() => navigate('/admin')} sx={{ color: '#951520' }} title="Admin">
                  <AdminPanelSettingsIcon />
                </IconButton>
              )}
              <IconButton onClick={e => setUserMenuAnchor(e.currentTarget)} sx={{ color: 'text.primary' }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: '#951520', fontSize: '0.875rem' }}>
                  {(user.name || user.email || '?')[0].toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu anchorEl={userMenuAnchor} open={Boolean(userMenuAnchor)}
                onClose={() => setUserMenuAnchor(null)}>
                {user.role !== 'admin' && (
                  <MenuItem onClick={() => { navigate('/i-miei-ordini'); setUserMenuAnchor(null); }}>
                    I miei ordini
                  </MenuItem>
                )}
                {user.role === 'admin' && (
                  <MenuItem onClick={() => { navigate('/admin'); setUserMenuAnchor(null); }}>
                    Admin panel
                  </MenuItem>
                )}
                <MenuItem onClick={() => { logout(); setUserMenuAnchor(null); navigate('/'); }}>
                  Esci
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Button variant="outlined" size="small" onClick={() => navigate('/accedi')}
              sx={{ borderColor: '#951520', color: '#951520', '&:hover': { bgcolor: '#951520', color: 'white' } }}>
              Accedi
            </Button>
          )}

          {isMobile && (
            <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: 'text.primary' }}>
              <MenuIcon />
            </IconButton>
          )}
        </Box>
      </Toolbar>

      {/* Mobile drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { mt: '86px' } }}>
        <Box sx={{ width: 240, pt: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1 }}>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          <List>
            {navLinks.map(link => (
              <ListItem
                button
                key={link.to}
                onClick={() => { navigate(link.to); setDrawerOpen(false); }}
                sx={{ py: 1.5 }}
              >
                <ListItemText primary={link.label} primaryTypographyProps={{ fontSize: '1.05rem' }} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Header;
